import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getBusinessById } from '@/api/endpoints/businesses';
import { Business } from '@/types/business';
import { nanoid } from 'nanoid';
import { NoteEntryState } from '../../notes/types';
import { CheckInEntryState } from '../types';
import type { CheckInDraft } from './use-drafts';

export const MAX_ENTRIES = 5;
export const MAX_NOTE_ENTRIES = 5;

export function createEmptyNoteEntry(): NoteEntryState {
  return {
    id: `note_entry_${nanoid()}`,
    menuItem: null,
    content: '',
    photo: null,
  };
}

export function createEmptyEntry(): CheckInEntryState {
  return {
    id: `entry_${nanoid()}`,
    menuItem: null,
    ratingId: null,
    ratingKey: null,
    photos: [],
    reviewContent: '',
    recommendTo: [],
  };
}

export interface QuickCheckInState {
  business: Business | null;
  diningType: 'dine_in' | 'takeout';
  occurredAt: string | null;
  entries: CheckInEntryState[];
}

export function buildInitialState(initialDraft?: CheckInDraft | null): QuickCheckInState {
  if (initialDraft) {
    if (initialDraft.entries && initialDraft.entries.length > 0) {
      return {
        business: initialDraft.business
          ? ({ id: initialDraft.business.id, name: initialDraft.business.name } as Business)
          : null,
        diningType: initialDraft.diningType ?? 'dine_in',
        occurredAt: null,
        entries: initialDraft.entries.map((e, index) => ({
          id: `draft_entry_${index}`,
          menuItem: e.menuItem
            ? ({ id: e.menuItem.id, name: e.menuItem.name } as any)
            : null,
          ratingId: e.ratingId,
          ratingKey: null,
          photos: e.photos.map((uri, pi) => ({
            id: `draft-photo-${index}-${pi}`,
            uri,
            isUploading: false,
          })),
          reviewContent: e.reviewContent,
          recommendTo: (e as any).recommendTo ?? [],
        })),
      };
    }

    return {
      business: initialDraft.business
        ? ({ id: initialDraft.business.id, name: initialDraft.business.name } as Business)
        : null,
      diningType: initialDraft.diningType ?? 'dine_in',
      occurredAt: null,
      entries: [
        {
          id: 'draft_entry_0',
          menuItem: initialDraft.menuItem
            ? ({ id: initialDraft.menuItem.id, name: initialDraft.menuItem.name } as any)
            : null,
          ratingId: initialDraft.ratingId,
          ratingKey: null,
          photos: initialDraft.photos.map((uri, index) => ({
            id: `draft-photo-${index}`,
            uri,
            isUploading: false,
          })),
          reviewContent: initialDraft.reviewContent,
          recommendTo: [],
        },
      ],
    };
  }

  return {
    business: null,
    diningType: 'dine_in',
    occurredAt: null,
    entries: [createEmptyEntry()],
  };
}

export interface UseCheckInFormStateOptions {
  initialDraft?: CheckInDraft | null;
  initialBusinessId?: string;
}

export function useCheckInFormState({
  initialDraft,
  initialBusinessId,
}: UseCheckInFormStateOptions = {}) {
  const [state, setState] = useState<QuickCheckInState>(() =>
    buildInitialState(initialDraft)
  );

  const [noteEntries, setNoteEntries] = useState<NoteEntryState[]>(() => [
    createEmptyNoteEntry(),
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const draftIdRef = useRef<string | null>(initialDraft?.id || null);

  const [lastSavedState, setLastSavedState] = useState<QuickCheckInState | null>(
    initialDraft ? state : null
  );

  const readyEntries = useMemo(
    () => state.entries.filter((e) => e.menuItem !== null && e.ratingId !== null),
    [state.entries]
  );

  const hasUnsavedChanges = useMemo(() => {
    const hasAnyData =
      state.business !== null ||
      state.entries.some(
        (e) =>
          e.menuItem !== null ||
          e.ratingId !== null ||
          e.photos.length > 0 ||
          e.reviewContent.trim().length > 0
      );

    if (!hasAnyData) return false;
    if (!lastSavedState) return true;

    if (state.business?.id !== lastSavedState.business?.id) return true;
    if (state.entries.length !== lastSavedState.entries.length) return true;

    return state.entries.some((entry, i) => {
      const saved = lastSavedState.entries[i];
      if (!saved) return true;
      return (
        entry.menuItem?.id !== saved.menuItem?.id ||
        entry.ratingId !== saved.ratingId ||
        entry.photos.length !== saved.photos.length ||
        entry.reviewContent !== saved.reviewContent
      );
    });
  }, [state, lastSavedState]);

  const canSubmit = useMemo(
    () => state.business !== null && readyEntries.length > 0,
    [state.business, readyEntries.length]
  );

  const readyNoteEntries = useMemo(
    () =>
      noteEntries.filter(
        (e) => e.menuItem !== null && e.content.trim().length > 0
      ),
    [noteEntries]
  );

  const canSubmitNotes = useMemo(
    () => state.business !== null && readyNoteEntries.length > 0,
    [state.business, readyNoteEntries.length]
  );

  useEffect(() => {
    if (!initialBusinessId || initialDraft || state.business) return;

    let cancelled = false;
    getBusinessById(initialBusinessId)
      .then((business) => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, business }));
        }
      })
      .catch((err) => {
        console.error('Failed to load initial business:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [initialBusinessId, initialDraft, state.business]);

  const handleSetBusiness = useCallback((business: Business) => {
    setState((prev) => ({
      ...prev,
      business,
      entries: [createEmptyEntry()],
    }));
    setNoteEntries([createEmptyNoteEntry()]);
  }, []);

  const handleClearBusiness = useCallback(() => {
    setState((prev) => ({
      ...prev,
      business: null,
      entries: [createEmptyEntry()],
    }));
    setNoteEntries([createEmptyNoteEntry()]);
  }, []);

  const handleSetDiningType = useCallback((diningType: 'dine_in' | 'takeout') => {
    setState((prev) => ({ ...prev, diningType }));
  }, []);

  const handleSetOccurredAt = useCallback((date: string | null) => {
    setState((prev) => ({ ...prev, occurredAt: date }));
  }, []);

  return {
    state,
    setState,
    noteEntries,
    setNoteEntries,
    isSubmitting,
    setIsSubmitting,
    isSavingDraft,
    setIsSavingDraft,
    draftIdRef,
    lastSavedState,
    setLastSavedState,
    readyEntries,
    hasUnsavedChanges,
    canSubmit,
    readyNoteEntries,
    canSubmitNotes,
    handleSetBusiness,
    handleClearBusiness,
    handleSetDiningType,
    handleSetOccurredAt,
  };
}
